/**
 * TipTap editor ↔ PowerSync orchestration for a page.
 *
 * - Initial load: `block` rows → TipTap document.
 * - Autosave: each local change is debounced and persisted via
 *   `syncPageBlocksMutation` (per-block diff: 1 edit = 1 row).
 * - Remote changes: reactive blocks query detects new rows from
 *   other devices/users; if editor is not focused and
 *   content differs, document is replaced.
 */
import { createEffect, createSignal, onCleanup, untrack } from "solid-js";
import type { Editor } from "@tiptap/core";
import { syncPageBlocksMutation } from "@folio/shared";
import { usePageBlocksQuery } from "@folio/shared/solid";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { SUGGESTION_SKIP_META } from "@/features/comments/lib/suggestion-mode";
import { blockNodesFingerprint, blocksToDoc, docToBlockNodes } from "../lib/doc-serializer";

const AUTOSAVE_DEBOUNCE_MS = 600;

export function usePageEditor(pageId: () => string) {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const { userId } = useSession();

  const [editor, setEditor] = createSignal<Editor | null>(null);
  const [isReady, setIsReady] = createSignal(false);
  const [isSaving, setIsSaving] = createSignal(false);
  // Timestamp of last successful local persistence. Feeds the
  // "Saved" indicator in detail view (autosave feedback).
  const [lastSavedAt, setLastSavedAt] = createSignal<number | null>(null);
  const [saveError, setSaveError] = createSignal(false);

  const blocks = usePageBlocksQuery({
    db: () => db(),
    drizzle,
    pageId: () => pageId(),
  });

  /** Fingerprint of the latest state persisted or loaded by the editor. */
  let lastSyncedFingerprint = "";
  let saveTimeout: ReturnType<typeof setTimeout> | null = null;
  let saveChain: Promise<void> = Promise.resolve();

  const persist = (instance: Editor) => {
    const dbInstance = drizzle();
    const uid = userId();
    const id = untrack(pageId);
    if (!dbInstance || !uid) {
      return;
    }
    const nodes = docToBlockNodes(instance.getJSON());
    const fingerprint = blockNodesFingerprint(nodes);
    if (fingerprint === lastSyncedFingerprint) {
      return;
    }
    lastSyncedFingerprint = fingerprint;
    setIsSaving(true);
    setSaveError(false);
    saveChain = saveChain
      .then(async () => {
        await syncPageBlocksMutation(dbInstance, id, uid, nodes);
        setLastSavedAt(Date.now());
        return undefined;
      })
      .catch((err: unknown) => {
        setSaveError(true);
        console.error(
          "[notes] Error saving blocks:",
          err instanceof Error ? err.message : String(err),
        );
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleDocUpdate = (instance: Editor) => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(() => {
      saveTimeout = null;
      persist(instance);
    }, AUTOSAVE_DEBOUNCE_MS);
  };

  const handleReady = (instance: Editor) => {
    setEditor(instance);
  };

  // Initial load: once blocks arrive, the initial document is
  // fixed and the editor can mount (isReady → route mounts PageEditor).
  createEffect(() => {
    if (!blocks.isLoading && !isReady()) {
      lastSyncedFingerprint = blockNodesFingerprint(
        docToBlockNodes(blocksToDoc(blocks.data ?? [])),
      );
      setIsReady(true);
    }
  });

  // Remote changes: apply only if they differ from what is displayed and user
  // is not actively typing (per-block last-write-wins resolved by server).
  createEffect(() => {
    const rows = blocks.data;
    const instance = editor();
    if (!rows || !instance || instance.isDestroyed || !instance.view || !isReady()) {
      return;
    }
    const remoteDoc = blocksToDoc(rows);
    const remoteFingerprint = blockNodesFingerprint(docToBlockNodes(remoteDoc));
    if (remoteFingerprint === lastSyncedFingerprint) {
      return;
    }
    if (instance.isFocused) {
      return;
    }
    lastSyncedFingerprint = remoteFingerprint;
    // Skip-meta: a remote PowerSync refresh must never be re-tracked as a
    // local suggestion by suggestion mode.
    try {
      instance.chain().setMeta(SUGGESTION_SKIP_META, true).setContent(remoteDoc).run();
    } catch {
      // Guard against destroyed / unmounting view
    }
  });

  // Flush on unmount (navigation): do not lose the last debounced edit.
  onCleanup(() => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
      saveTimeout = null;
      const instance = editor();
      if (instance && !instance.isDestroyed) {
        persist(instance);
      }
    }
    setEditor(null);
  });

  return {
    isReady,
    isSaving,
    lastSavedAt,
    saveError,
    initialDoc: () => blocksToDoc(blocks.data ?? []),
    handleDocUpdate,
    handleReady,
  };
}
